import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Activity, ArrowUpRight, Target, ShieldCheck, Zap, Wallet,
  Camera, Mail, Sparkles, TrendingUp, ChevronRight, Clock, BarChart3, Upload
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip, CartesianGrid, YAxis } from 'recharts';
import { motion } from 'framer-motion';

import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../translations/translations'; // Eğer bu dosya yoksa kaldırılabilir, context yeterli
import { apiService } from '../services/api';
import { useModals } from '../context/ModalContext'; // Eğer modal context kullanılıyorsa
import { Badge } from '../components/ui/badge';
import TimelineWidget from '../components/TimelineWidget';
import HeaderActions from '../components/HeaderActions';
import Footer from '../components/Footer';
import OnboardingWizard from '../components/OnboardingWizard';
import QuickScanModal from '../components/QuickScanModal'; // 🔥 YENİ: Modal eklendi

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { language, t } = useLanguage(); // t fonksiyonunu context'ten alıyoruz
  const { openModal } = useModals ? useModals() : { openModal: () => {} }; // Güvenli erişim

  const [isLoading, setIsLoading] = useState(true);
  const [allDocuments, setAllDocuments] = useState([]);
  const [timeFilter, setTimeFilter] = useState('year');
  const [isScanOpen, setIsScanOpen] = useState(false); // 🔥 Modal kontrolü

  // Onboarding Kontrolü
  const showOnboarding = useMemo(() => {
    return user && user.onboarding?.completed !== true;
  }, [user]);

  // Veri Çekme
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const response = await apiService.documents.getAll();
        if (isMounted) setAllDocuments(response.data || []);
      } catch (error) { 
        console.error("Dashboard data fetch error:", error); 
      } finally { 
        if (isMounted) setIsLoading(false); 
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  // Dashboard Verileri
  const dashboardData = useMemo(() => {
    const totalSpent = allDocuments.reduce((acc, doc) => acc + (doc.amount || 0), 0);
    const totalRefund = totalSpent * 0.185; 
    
    const months = language === 'tr' 
        ? ['OCA', 'ŞUB', 'MAR', 'NİS', 'MAY', 'HAZ', 'TEM', 'AĞU', 'EYL', 'EKİ', 'KAS', 'ARA']
        : ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    
    const chartDataMap = new Map();
    months.forEach(m => chartDataMap.set(m, 0));

    allDocuments.forEach(doc => {
      if (doc.date) {
        const date = new Date(doc.date);
        const monthName = months[date.getMonth()];
        chartDataMap.set(monthName, (chartDataMap.get(monthName) || 0) + (doc.amount || 0));
      }
    });

    return { 
      stats: { spent: totalSpent, refund: totalRefund, docsCount: allDocuments.length, efficiency: 98.4 }, 
      recent: allDocuments.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5), 
      chart: months.map(name => ({ name, amount: chartDataMap.get(name) })) 
    };
  }, [allDocuments, language]);

  const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);

  const containerVars = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } };
  const itemVars = { hidden: { y: 15, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: "spring", damping: 20 } } };

  return (
    <motion.div 
      initial="hidden" animate="visible" variants={containerVars}
      className="min-h-screen bg-[#0B1120] text-slate-100 font-sans flex flex-col relative overflow-x-hidden"
    >
      {/* Onboarding Sihirbazı */}
      {showOnboarding && (
        <OnboardingWizard onComplete={() => refreshUser()} />
      )}

      {/* Arka Plan Efekti */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/5 blur-[120px] rounded-full -z-10" />

      {/* HEADER */}
      <header className="relative z-50 pt-10 pb-8 px-6 max-w-[1600px] mx-auto w-full">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
          <div className="space-y-2">
            <motion.div variants={itemVars} className="flex items-center gap-3">
                <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest">Enterprise v4.0</Badge>
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-500 uppercase tracking-widest">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    {t('system_online') || 'SYSTEM ONLINE'}
                </div>
            </motion.div>
            <motion.h1 variants={itemVars} className="text-4xl md:text-5xl font-black tracking-tighter text-white italic uppercase leading-none">
              {t('welcome_morning')?.split(' ')[0] || 'Welcome'} <span className="text-blue-500">{user?.name?.split(' ')[0] || 'User'}</span>
            </motion.h1>
          </div>
          
          <motion.div variants={itemVars} className="flex items-center gap-4 w-full lg:w-auto">
            <div className="glass-panel p-1.5 rounded-2xl flex border-white/5 bg-white/5 backdrop-blur-md">
              {['year', 'month'].map((f) => (
                <button key={f} onClick={() => setTimeFilter(f)} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${timeFilter === f ? 'bg-blue-600 text-white shadow-xl shadow-blue-900/40' : 'text-slate-400 hover:text-white'}`}>
                  {f === 'year' ? 'Fiscal 2025' : 'Current'}
                </button>
              ))}
            </div>
            <HeaderActions />
          </motion.div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="flex-1 w-full max-w-[1600px] mx-auto px-6 relative z-10 space-y-8 pb-24">
        
        {/* KPI GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KpiCard icon={<Wallet size={22}/>} title={t('total_expenses') || 'Total Expenses'} value={formatCurrency(dashboardData.stats.spent)} color="blue" isLoading={isLoading} subValue="+12.5% vs last month" />
          <KpiCard icon={<Sparkles size={22}/>} title={t('refund_potential') || 'Refund Potential'} value={formatCurrency(dashboardData.stats.refund)} color="orange" isLoading={isLoading} trend={<TrendingUp size={14}/>} subValue="Based on AI projection" />
          <KpiCard icon={<FileText size={22}/>} title={t('total_documents') || 'Total Docs'} value={dashboardData.stats.docsCount} color="purple" isLoading={isLoading} subValue="Fully indexed & secure" />
          <KpiCard icon={<Zap size={22}/>} title="AI Efficiency" value={`%${dashboardData.stats.efficiency}`} color="emerald" isLoading={isLoading} subValue="Zero-touch processing" />
        </div>

        {/* CHART & QUICK ACTIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div variants={itemVars} className="lg:col-span-2 glass-panel rounded-[3rem] p-10 border-white/5 relative overflow-hidden shadow-2xl">
                <div className="flex justify-between items-start mb-10">
                    <div>
                        <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter flex items-center gap-3">
                            <BarChart3 size={24} className="text-blue-500" /> Financial <span className="text-blue-400">Trajectory</span>
                        </h3>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-[0.2em] mt-2">Cash flow & Tax optimization intelligence</p>
                    </div>
                </div>
                
                <div className="h-[350px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dashboardData.chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorSpent" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="8 8" vertical={false} stroke="rgba(255,255,255,0.02)" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10, fontWeight: '800' }} dy={15}/>
                            <YAxis hide domain={[0, 'dataMax + 1000']} />
                            <Tooltip content={<CustomTooltip formatCurrency={formatCurrency}/>} />
                            <Area type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={4} fillOpacity={1} fill="url(#colorSpent)" animationDuration={2000} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </motion.div>

            <div className="space-y-6">
                <motion.div variants={itemVars} className="glass-panel rounded-[3rem] p-8 text-white shadow-2xl relative overflow-hidden flex flex-col justify-between border-white/5 bg-gradient-to-br from-blue-600/20 to-transparent">
                    <h3 className="font-black text-2xl italic uppercase tracking-tighter mb-8">{t('quick_terminal') || 'Quick Terminal'}</h3>
                    <div className="space-y-4">
                        
                        {/* 🔥 SCAN BUTTON - Modal'ı tetikler */}
                        <button onClick={() => setIsScanOpen(true)} className="w-full py-8 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-[2rem] flex flex-col items-center justify-center gap-4 transition-all group/btn shadow-2xl active:scale-[0.97]">
                            <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl group-hover/btn:scale-110 transition-transform duration-500 border border-white/10"><Camera size={32} className="text-white" /></div>
                            <span className="font-black uppercase tracking-[0.3em] text-[11px]">{t('smart_scan') || 'Smart Scan'}</span>
                        </button>

                        <div className="grid grid-cols-2 gap-4">
                            <QuickActionBtn onClick={() => navigate('/create-invoice')} icon={<FileText size={20} />} label={t('invoice') || 'Invoice'} />
                            <QuickActionBtn onClick={() => navigate('/archive')} icon={<Upload size={20} />} label={t('action_upload') || 'Upload'} />
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>

        {/* TIMELINE & RECENT ACTIVITY */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
                <motion.div variants={itemVars} className="glass-panel rounded-[3rem] overflow-hidden border-white/5 shadow-2xl">
                    <div className="px-10 py-8 border-b border-white/5 flex justify-between items-center">
                        <h3 className="font-black text-white text-base uppercase tracking-[0.2em] italic flex items-center gap-4">
                            <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" /> {t('live_feed') || 'Live Feed'}
                        </h3>
                        <button onClick={() => navigate('/archive')} className="text-[10px] font-black text-slate-500 hover:text-blue-400 uppercase tracking-[0.2em] transition-all flex items-center gap-2 group">
                            {t('view_all_documents') || 'View All'} <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </button>
                    </div>
                    <div className="p-6"><TimelineWidget documents={allDocuments} isLoading={isLoading} /></div>
                </motion.div>
            </div>
            <motion.div variants={itemVars} className="space-y-6">
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] ml-4 flex items-center gap-2"><Clock size={12}/> Recent Vault</h4>
                <div className="space-y-3">
                  {dashboardData.recent.map((doc, idx) => (
                    <RecentItem key={doc.id || idx} doc={doc} formatCurrency={formatCurrency} navigate={navigate} />
                  ))}
                </div>
            </motion.div>
        </div>
      </main>
      
      {/* 🔥 QUICK SCAN MODAL */}
      <QuickScanModal isOpen={isScanOpen} onClose={() => setIsScanOpen(false)} />

      <Footer />
    </motion.div>
  );
};

// --- SUB-COMPONENTS ---
const KpiCard = ({ icon, title, value, color, isLoading, trend, subValue }) => {
  const colorMap = {
    blue: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
    orange: 'text-orange-500 bg-orange-500/10 border-orange-500/20',
    purple: 'text-purple-500 bg-purple-500/10 border-purple-500/20',
    emerald: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20'
  };
  return (
    <motion.div whileHover={{ y: -5 }} className={`glass-panel p-8 rounded-[2.5rem] border ${colorMap[color].split(' ')[2]} relative overflow-hidden group shadow-xl bg-[#111827]/60`}>
      <div className="flex justify-between items-start mb-6">
        <div className={`p-4 rounded-2xl transition-transform group-hover:scale-110 ${colorMap[color].split(' ').slice(0, 2).join(' ')}`}>{icon}</div>
        {trend && <div className="text-emerald-500 bg-emerald-500/10 p-1.5 rounded-lg">{trend}</div>}
      </div>
      <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] mb-1">{title}</p>
      <h3 className="text-3xl font-black text-white tracking-tighter">{isLoading ? "..." : value}</h3>
      <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest mt-4 flex items-center gap-2"><ShieldCheck size={10}/> {subValue}</p>
    </motion.div>
  );
};

const QuickActionBtn = ({ onClick, icon, label }) => (
  <button onClick={onClick} className="bg-[#0B1120] hover:bg-white/5 h-28 rounded-[2rem] flex flex-col items-center justify-center gap-3 transition-all border border-white/5 group shadow-inner active:scale-95">
    <div className="p-3 rounded-xl bg-white/5 text-slate-400 group-hover:text-white transition-colors">{icon}</div>
    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 group-hover:text-slate-200">{label}</span>
  </button>
);

const RecentItem = ({ doc, formatCurrency, navigate }) => (
  <div onClick={() => navigate('/archive')} className="flex items-center justify-between p-5 bg-white/[0.02] hover:bg-white/[0.05] rounded-[1.5rem] cursor-pointer transition-all border border-white/5 active:scale-[0.98]">
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 bg-[#0B1120] rounded-xl flex items-center justify-center text-blue-500"><FileText size={18} /></div>
      <div>
        <h5 className="font-bold text-slate-200 text-xs truncate w-24">{doc.merchant_name || 'Document'}</h5>
        <p className="text-[9px] font-black text-slate-600 uppercase tracking-tighter mt-0.5">{doc.date ? new Date(doc.date).toLocaleDateString('de-DE') : 'Pending'}</p>
      </div>
    </div>
    <div className="text-right">
      <p className="text-sm font-black text-white">{formatCurrency(doc.amount || 0)}</p>
      <ChevronRight size={14} className="text-slate-800 ml-auto" />
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, formatCurrency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0f172a] border border-white/10 p-5 rounded-[1.5rem] shadow-2xl">
        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">{payload[0].payload.name}</p>
        <p className="text-lg font-black text-white">{formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export default Dashboard;